import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../../../config/firebase";
import getMoodImages from "utils/moods/getMoodImages";

export type MoodImage = { enLabel: string; src?: string } & Record<
  string,
  unknown
>;

export function useMoodImages(
  theme?: string,
  collection?: string,
  selectedDate?: string | Date
) {
  const { t, i18n } = useTranslation();

  const baseMoodImages: MoodImage[] = useMemo(
    () => getMoodImages(theme, collection, t, selectedDate),
    [theme, t, selectedDate]
  );

  const [resolvedUrls, setResolvedUrls] = useState<string[]>([]);

  useEffect(() => {
    const storagePaths = baseMoodImages
      .map((m) => m?.src)
      .filter(
        (s): s is string => typeof s === "string" && !s.startsWith("http")
      );
    if (storagePaths.length === 0) {
      setResolvedUrls(baseMoodImages.map((m) => m?.src ?? ""));
      return;
    }
    let cancelled = false;
    Promise.all(
      storagePaths.map((path) => getDownloadURL(ref(storage, path)))
    ).then((urls) => {
      if (!cancelled) setResolvedUrls(urls);
    });
    return () => {
      cancelled = true;
    };
  }, [baseMoodImages]);

  const moodImages: MoodImage[] = useMemo(() => {
    return baseMoodImages.map((m, i) => ({
      ...m,
      src: resolvedUrls[i] ?? m.src,
    }));
  }, [baseMoodImages, resolvedUrls]);

  const feelingNames = useMemo(
    () => moodImages.map((m) => m.enLabel),
    [moodImages]
  );

  const tEn = i18n.getFixedT("en");

  // Preload top images to improve first render
  const [imagesReady, setImagesReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const imgs = (moodImages || [])
      .slice(0, 4)
      .map((m) => m?.src)
      .filter(Boolean) as string[];
    if (imgs.length === 0) {
      setImagesReady(true);
      return;
    }

    let loaded = 0;
    const mark = () => {
      loaded += 1;
      if (!cancelled && loaded >= Math.min(2, imgs.length)) {
        // Consider ready after first couple images for faster paint
        setImagesReady(true);
      }
    };

    imgs.forEach((href) => {
      try {
        // Inject <link rel="preload" as="image"> for critical images
        const id = `preload-img-${href}`;
        if (typeof document !== "undefined" && !document.getElementById(id)) {
          const link = document.createElement("link");
          link.rel = "preload";
          link.as = "image";
          link.href = href;
          link.id = id;
          document.head.appendChild(link);
        }
      } catch {
        /* noop */
      }

      const im = new Image();
      im.decoding = "async";
      // @ts-ignore: fetchPriority not yet typed in TS lib
      im.fetchPriority = "high";
      // keep defaults
      im.onload = mark;
      im.onerror = mark;
      im.src = href;
    });

    return () => {
      cancelled = true;
    };
  }, [moodImages]);

  return { moodImages, feelingNames, tEn, imagesReady };
}
